import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FORMLY_FIELD_TYPES } from '@app/constants';
import { CellTransform, FilterConfig, TableConfig } from '@app/interfaces';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { FormComponent } from '@app/shared/forms/components/form.component';
import { isNil } from '@app/utils';
import * as Transforms from '@app/utils/transforms';
import { SetupBudgetImportStatusModel } from '../interfaces/setup-budget-import-status.model';

@Component({
	selector: 'setup-budget-import-confirm-modal',
	template: ` <app-modal>
		<sl-form [form]="form" [fields]="formlyFields" [model]="model" [options]="formlyFormOptions"></sl-form>
	</app-modal>`,
	styleUrls: ['./setup-budget-import-confirm-modal.component.less'],
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [ModalComponent, FormComponent],
})
export class SetupBudgetImportConfirmModalComponent implements OnInit {
	form = new UntypedFormGroup({});
	formlyFormOptions: FormlyFormOptions = {
		formState: {
			mode: 'edit',
			selects: {
				tableData: [],
			},
		},
	};
	formlyFields: FormlyFieldConfig[] = [];
	model: any = {};
	filters = {
		Invalid: false,
	};

	get filterFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'row sbiv-container',
				fieldGroup: [
					{
						key: 'Invalid',
						className: 'col-xs-12 tb-filter',
						type: FORMLY_FIELD_TYPES.select,
						templateOptions: {
							label: 'File Account Import Status',
							options: [
								{ value: true, label: 'Failed due to error' },
								{ value: false, label: 'Imported successfully' },
							],
						},
					},
				],
			},
		];
	}

	filtersConfig: FilterConfig<SetupBudgetImportStatusModel>[] = [
		{
			key: 'Invalid',
			transform: (chip) => {
				switch (chip.value) {
					case true:
						return 'File Account Import Status: Failed due to error';
					case false:
						return 'File Account Import Status: Imported successfully';
				}
			},
		},
	];

	constructor(
		protected readonly dialog: MatDialog,
		public dialogRef: MatDialogRef<SetupBudgetImportConfirmModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		this.formlyFormOptions.formState.selects.tableData = this.data?.tableData;
	}

	ngOnInit(): void {
		this.formlyFields = this.getFormlyFields();
	}

	getFormlyFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'row sbiv-container',
				fieldGroup: [
					{
						fieldGroupClassName: 'col-xs-12',
						fieldGroup: [
							{
								key: 'ResultsTable',
								type: FORMLY_FIELD_TYPES.formlyTable,
								className: 'col-xs-12',
								templateOptions: {
									noDataText: 'No Accounts Found',
									data: [],
									required: true,
									activeFilter: true,
									filters: this.filters,
									filterFields: this.filterFields,
									filtersConfig: this.filtersConfig,
									searchOnly: false,
									hideSearch: false,
									showChipKey: false,
								},
								expressionProperties: {
									'templateOptions.config': () => this.tableConfig,
									'templateOptions.data': () => this.formlyFormOptions.formState.selects.tableData,
								},
							},
						],
					},
				],
			},
		];
	}

	get tableConfig(): TableConfig<SetupBudgetImportStatusModel> {
		return {
			columns: [
				{
					header: 'Account #',
					key: 'AccountNumber',
				},
				{
					header: 'Budget Total',
					key: 'BudgetTotal',
					transform: ({ value }: CellTransform<any>): string => {
						if (isNil(value) || isNaN(Number(value)) || value.trim().length === 0 || !/^\d+(\.\d{1,2})?$/.test(value)) {
							return value;
						} else {
							return Transforms.getFormattedNumber(value, 2);
						}
					},
				},
				{
					cellClass: (row: SetupBudgetImportStatusModel) => {
						const className = row.Invalid ? 'text-danger' : 'text-success';
						return className;
					},
					header: 'Message',
					key: 'Message',
				},
				{
					header: 'Invalid',
					key: 'Invalid',
					hidden: true,
				},
			],
			onRowClick: () => {},
			stickyHeader: true,
			showRowSelect: false,
			bulkActions: [],
			defaultSortDirection: 'desc',
			defaultSortField: 'Invalid',
			defaultPageSize: 50,
			excludePaginator: this.formlyFormOptions.formState.selects.tableData.length <= 10,
		};
	}
}
